/**
 * Carousel state for the Product Catalog hero.
 *
 * Ported from the Learning Center Discovery Hub hero (origin/labs
 * useHeroBanner): auto-rotation with hover pause, wrap-around prev/next,
 * arrow-key navigation, and index clamping when the slide count changes.
 */

import { useState, useEffect, useCallback, useRef } from "react";

interface UseCatalogHeroOptions {
  itemCount: number;
  autoRotateInterval?: number;
  enabled?: boolean;
}

interface UseCatalogHeroReturn {
  currentIndex: number;
  goToSlide: (index: number) => void;
  goToNext: () => void;
  goToPrevious: () => void;
  pauseRotation: () => void;
  resumeRotation: () => void;
  isPaused: boolean;
}

export function useCatalogHero({
  itemCount,
  autoRotateInterval = 8000,
  enabled = true,
}: UseCatalogHeroOptions): UseCatalogHeroReturn {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const goToSlide = useCallback(
    (index: number) => {
      if (index >= 0 && index < itemCount) {
        setCurrentIndex(index);
      }
    },
    [itemCount],
  );

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev >= itemCount - 1 ? 0 : prev + 1));
  }, [itemCount]);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev <= 0 ? itemCount - 1 : prev - 1));
  }, [itemCount]);

  const pauseRotation = useCallback(() => {
    setIsPaused(true);
  }, []);

  const resumeRotation = useCallback(() => {
    setIsPaused(false);
  }, []);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!enabled || isPaused || itemCount <= 1) {
      clearTimer();
      return;
    }

    timerRef.current = setInterval(() => {
      goToNext();
    }, autoRotateInterval);

    return () => {
      clearTimer();
    };
  }, [enabled, isPaused, itemCount, autoRotateInterval, goToNext, clearTimer]);

  useEffect(() => {
    if (!enabled || itemCount <= 1) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement
      ) {
        return;
      }

      switch (event.key) {
        case "ArrowLeft":
          goToPrevious();
          break;
        case "ArrowRight":
          goToNext();
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [enabled, itemCount, goToNext, goToPrevious]);

  useEffect(() => {
    if (currentIndex >= itemCount && itemCount > 0) {
      setCurrentIndex(0);
    }
  }, [itemCount, currentIndex]);

  return {
    currentIndex,
    goToSlide,
    goToNext,
    goToPrevious,
    pauseRotation,
    resumeRotation,
    isPaused,
  };
}

export default useCatalogHero;
